import React, { useState, useEffect } from 'react';
import SidebarLayout from "components/Layout/Layout";
import { API_URL } from "env";
import { useNavigate } from 'react-router-dom';
import { PencilSimpleIcon, TrashSimpleIcon } from '@phosphor-icons/react';
import { Table, Input, Button, Modal } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

interface RecommendationItem {
    id: number;
    title: string;
}

const ListRecomendation: React.FC = () => {
    const [data, setData] = useState<RecommendationItem[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(() => {
        const saved = sessionStorage.getItem('rcmPage');
        return saved ? Number(saved) : 1;
    });
    const [totalItems, setTotalItems] = useState(0);
    const [count, setCount] = useState(0);
    const [count2, setCount2] = useState(0);
    const [searchText, setSearchText] = useState('');

    const fetchData = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            let url = `${API_URL}api/v1/be/recommendations?page=${page}&per_page=${limit}`;
            if (searchText.trim()) url += `&title=${encodeURIComponent(searchText)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const json = await response.json();
            setData(json.data.data || []);
            setCurrentPage(json.data.current_page || 1);
            setTotalItems(json.data.total || 0);
        } catch (err) {
            console.error("Lỗi khi gọi API:", err);
        } finally {
            setLoading(false);
        }
    };

    const clickPagination = (key: number) => {
        setCurrentPage(key);
        sessionStorage.setItem('rcmPage', JSON.stringify(key));
    };

    useEffect(() => {
        const saved = sessionStorage.getItem('rcmPage');
        if (saved) setCurrentPage(Number(saved));
        else setCurrentPage(1);
    }, []);

    useEffect(() => {
        if (typeof currentPage === 'number') {
            fetchData(currentPage);
            setCount(prev => prev + 1);
        }
    }, [currentPage, count2]);

    useEffect(() => {
        if (count > 0) {
            setCurrentPage(1);
            setCount2(prev => prev + 1);
        }
    }, [searchText]);

    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xóa mục này không?",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await fetch(`${API_URL}api/v1/be/recommendations/${id}`, {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    });
                    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                    const json = await response.json();
                    if (json.status) {
                        Modal.success({ title: "Thành công", content: "Xóa thành công!" });
                        await fetchData(currentPage);
                    } else {
                        Modal.error({ title: "Thất bại", content: "Xóa thất bại: " + (json.message || "Unknown error") });
                    }
                } catch (error: unknown) {
                    Modal.error({ title: "Lỗi", content: "Lỗi khi xóa: " + (error instanceof Error ? error.message : String(error)) });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const columns: ColumnsType<RecommendationItem> = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        {
            title: 'Name',
            key: 'name',
            render: (_, item) => {
                try {
                    const title = JSON.parse(item.title);
                    return title.vi || title.en;
                } catch {
                    return item.title;
                }
            },
        },
        {
            title: 'Action',
            key: 'actions',
            width: 100,
            render: (_, item) => (
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate(`/add-recomendation/edit/${item.id}`)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors cursor-pointer"
                    >
                        <PencilSimpleIcon size={16} />
                    </button>
                    <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900 transition-colors cursor-pointer text-red-500"
                    >
                        <TrashSimpleIcon size={16} />
                    </button>
                </div>
            ),
        },
    ];

    const handleTableChange = (pag: TablePaginationConfig) => {
        clickPagination(pag.current ?? 1);
    };

    return (
        <SidebarLayout
            title={<h4 className="text-2xl font-bold m-0! py-4">Recommendation</h4>}
        >
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <Input.Search
                        placeholder="Search..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                        className="w-64"
                    />
                    <Button
                        type="primary"
                        className="ml-auto"
                        onClick={() => navigate('/add-recomendation')}
                    >
                        + Add
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: 10,
                        total: totalItems,
                        showSizeChanger: false,
                        showTotal: total => `Total ${total} items`,
                    }}
                    onChange={handleTableChange}
                />
            </div>
        </SidebarLayout>
    );
};

export default ListRecomendation;
